/**
 * Shared, non-document state of the Song & Timing feature (W2-08 timing): one per app (keyed by the editor store,
 * which is the same object for every command context and panel of an app).
 *
 *  - tap tempo series (the B key and the Tap button share it);
 *  - the "Too early / Too late" step index;
 *  - per project, whether a tempo or offset edit happened this session and how many other difficulties share its
 *    song (the "other difficulties still use the old tempo map" notice, DESIGN.md §4.15 / §5.16): keyed by project,
 *    so opening another project never shows the notice of the one edited before;
 *  - the one-time latency banner (DESIGN.md §5.2 "Latency calibration");
 *  - "Play with clicks" (playback started by the helper, metronome forced on until it stops);
 *  - the bar-by-bar view: first bar shown, rows, locked region.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { TempoEditPolicy } from '../../core/commands/types';
import type { AssetId, ProjectId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import { EMPTY_TAPS, type TapState } from './timingModel';

export interface AlignmentViewState {
  /** First bar shown (bar numbers as core/time/meter: 1 = the bar containing beat 0). */
  readonly firstBar: number;
  /** Locked bar range, or null (rows follow the playhead while playing). */
  readonly locked: { readonly from: number; readonly to: number } | null;
}

export interface TimingSessionState {
  readonly taps: TapState;
  readonly byEarIndex: number;
  /**
   * Projects with a tempo or offset edit this session, each with the number of other difficulties that share its
   * song (0 until counted).
   */
  readonly timingEdited: ReadonlyMap<ProjectId, number>;
  readonly latencyBanner: boolean;
  readonly clicks: boolean;
  /** Metronome state before "Play with clicks" turned it on (restored when it stops). */
  readonly clicksRestoreMetronome: boolean | null;
  readonly alignment: AlignmentViewState;
  /** Songs whose tempo was detected automatically this session ("automatic on first visit"). */
  readonly autoDetected: ReadonlySet<AssetId>;
  /** Music time under the pointer in the song overview (the `timing.beatZeroHere` key), or null. */
  readonly overviewHoverSec: number | null;
}

export interface TimingSession {
  get(): TimingSessionState;
  set(patch: Partial<TimingSessionState> | ((s: TimingSessionState) => Partial<TimingSessionState>)): void;
  subscribe(cb: () => void): Unsubscribe;
}

const INITIAL: TimingSessionState = {
  taps: EMPTY_TAPS,
  byEarIndex: 0,
  timingEdited: new Map(),
  latencyBanner: false,
  clicks: false,
  clicksRestoreMetronome: null,
  alignment: { firstBar: 1, locked: null },
  autoDetected: new Set(),
  overviewHoverSec: null,
};

const sessions = new WeakMap<object, TimingSession>();

export function createTimingSession(): TimingSession {
  let state = INITIAL;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(p) {
      const next = typeof p === 'function' ? p(state) : p;
      state = { ...state, ...next };
      for (const cb of [...listeners]) cb();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

/** The session of an app. */
export function timingSessionFor(services: Pick<AppServices, 'editor'>): TimingSession {
  let s = sessions.get(services.editor);
  if (s === undefined) {
    s = createTimingSession();
    sessions.set(services.editor, s);
  }
  return s;
}

export function useTimingSession<T>(services: Pick<AppServices, 'editor'>, select: (s: TimingSessionState) => T): T {
  const session = timingSessionFor(services);
  const subscribe = useCallback((cb: () => void) => session.subscribe(cb), [session]);
  const get = useCallback(() => select(session.get()), [session, select]);
  return useSyncExternalStore(subscribe, get, get);
}

// ---------------------------------------------------------------------------------------------------------------
// Edit policy (per project: EditorStoreState.tempoPolicy, persisted with the view state, shared with the inspector)
// ---------------------------------------------------------------------------------------------------------------

export function setPolicy(services: Pick<AppServices, 'editor'>, policy: TempoEditPolicy): void {
  services.editor.getState().setTempoPolicy(policy);
}

/** The policy in force for the open project. */
export function currentPolicy(services: Pick<AppServices, 'editor'>): TempoEditPolicy {
  return services.editor.getState().tempoPolicy;
}

// ---------------------------------------------------------------------------------------------------------------
// Other difficulties of the song
// ---------------------------------------------------------------------------------------------------------------

/** Counts the other (non-trashed) projects that share the open project's song. */
export async function countOtherDifficulties(services: Pick<AppServices, 'persist' | 'store'>): Promise<number> {
  const store = services.store;
  const music = store?.doc.meta.music ?? null;
  if (store === null || music === null) return 0;
  try {
    const list = await services.persist.listProjects(false);
    return list.filter((p) => p.id !== store.projectId && p.musicAsset === music && p.trashedAt === null).length;
  } catch {
    return 0;
  }
}

/**
 * Notes a tempo or offset edit of the open project (shows the other-difficulties notice when its song has some).
 * The count is taken at the project's first timing edit of the session.
 */
export function noteTimingEdit(services: Pick<AppServices, 'editor' | 'persist' | 'store'>): void {
  const store = services.store;
  if (store === null) return;
  const project = store.projectId;
  const session = timingSessionFor(services);
  if (session.get().timingEdited.has(project)) return;
  const withProject = (count: number) => (st: TimingSessionState) => {
    const next = new Map(st.timingEdited);
    next.set(project, count);
    return { timingEdited: next };
  };
  session.set(withProject(0));
  void countOtherDifficulties(services).then((n) => {
    if (n > 0) session.set(withProject(n));
  });
}

/** Number of other difficulties to warn about for `project` (0 while it has no timing edit this session). */
export function otherDifficultiesNotice(st: TimingSessionState, project: ProjectId | null): number {
  return project === null ? 0 : (st.timingEdited.get(project) ?? 0);
}
